import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SchoolIcon from '@mui/icons-material/School';
import MoneyIcon from '@mui/icons-material/LocalAtm';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Page from '../../components/Layout/Page';
import useStyles from './styles';
import FinancialDashboard from './financial';
import AcademicDashboard from './academic';
import { ReduxState } from '../../types';

type DashboardTab = 'financial' | 'academic';

const Dashboard: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state: ReduxState) => state.account.user);

  const canViewFinancial = user?.canViewFinancialIndicators ?? false;
  const canViewAcademic = user?.canViewAcademicIndicators ?? false;

  const getInitialTab = (): DashboardTab => {
    if (location.pathname.includes('/academico') && canViewAcademic) {
      return 'academic';
    }
    if (location.pathname.includes('/financiero') && canViewFinancial) {
      return 'financial';
    }
    if (canViewFinancial) return 'financial';
    if (canViewAcademic) return 'academic';
    return 'financial';
  };

  const initialTab = getInitialTab();
  const [activeTab, setActiveTab] = useState<DashboardTab>(initialTab);

  useEffect(() => {
    if (!canViewFinancial && !canViewAcademic) {
      history.push('/');
      return;
    }
    if (activeTab === 'financial' && !canViewFinancial && canViewAcademic) {
      setActiveTab('academic');
      history.push('/dashboard/academico');
    } else if (activeTab === 'academic' && !canViewAcademic && canViewFinancial) {
      setActiveTab('financial');
      history.push('/dashboard/financiero');
    }
  }, [canViewFinancial, canViewAcademic, activeTab, history]);

  const handleChangeTab = (_: React.SyntheticEvent, value: DashboardTab | string) => {
    const newTab = value as DashboardTab;
    setActiveTab(newTab);
    const newPath = newTab === 'financial' ? '/dashboard/financiero' : '/dashboard/academico';
    history.push(newPath);
  };

  return (
    <Page title="Dashboard" className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerTitle}>
          <DashboardIcon fontSize="large" />
          <Typography
            className={classes.headerTitleText}
          >
            Dashboard
          </Typography>
        </Box>

        {(canViewFinancial || canViewAcademic) && (
          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            className={classes.tabs}
            TabIndicatorProps={{ className: classes.tabIndicator }}
          >
            {canViewFinancial && (
              <Tab
                value="financial"
                label="Dashboard financiero"
                icon={<MoneyIcon />}
                iconPosition="start"
                className={classes.tab}
              />
            )}
            {canViewAcademic && (
              <Tab
                value="academic"
                label="Dashboard académico"
                icon={<SchoolIcon />}
                iconPosition="start"
                className={classes.tab}
              />
            )}
          </Tabs>
        )}
      </Box>

      {activeTab === 'financial' && canViewFinancial && <FinancialDashboard />}
      {activeTab === 'academic' && canViewAcademic && <AcademicDashboard />}
    </Page>
  );
};

export default Dashboard;
